/**
 * Chargement différé résistant aux déploiements.
 *
 * Les fichiers construits portent un nom différent à chaque version. Quand une nouvelle version est publiée
 * pendant qu'un onglet est ouvert, le fichier que cet onglet connaît n'existe plus : la carte, chargée à la
 * demande, ne s'affiche alors jamais. On récupère la version à jour en rechargeant la page une seule fois.
 * Le drapeau posé en session empêche toute boucle si l'échec vient d'autre chose, une coupure réseau par exemple.
 */
const RELOAD_FLAG = 'ermont-live:chunk-reload';

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) !== null;
  } catch {
    // Stockage refusé (navigation privée) : on considère qu'aucun rechargement n'a eu lieu.
    return false;
  }
}

function writeFlag(present: boolean): void {
  try {
    if (present) sessionStorage.setItem(RELOAD_FLAG, '1');
    else sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // Sans stockage, le rechargement reste possible une fois par chargement de page.
  }
}

async function reloadOnce(): Promise<never> {
  writeFlag(true);
  // Le service worker garde peut-être l'ancienne page en cache : on lui demande de se mettre à jour d'abord.
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined);
    await registration?.update().catch(() => undefined);
  }
  window.location.reload();
  // La page est en train d'être remplacée : cette promesse ne se résout jamais, ce qui laisse
  // l'écran de chargement affiché au lieu d'une erreur fugitive.
  return new Promise<never>(() => {});
}

/** Enveloppe un `import()` différé pour survivre à une mise en ligne pendant la visite. */
export function lazyChunk<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().then(
      (module) => {
        writeFlag(false);
        return module;
      },
      (error: unknown) => {
        if (readFlag()) throw error;
        return reloadOnce();
      },
    );
}
